import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { requestsAPI, usersAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Receipt, ArrowLeft, CheckCircle, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function RequestMoneyPage() {
  const { user } = useAuth();
  const { logo, t } = useTheme();
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleSearch = async (query) => {
    setEmail(query);
    if (query.length < 2) { setSearchResults([]); return; }
    try { const response = await usersAPI.search(query); setSearchResults(response.data.filter(u => u.user_id !== user?.user_id)); } catch (err) { console.error('User search failed:', err); }
  };

  const selectUser = (u) => { setSelectedUser(u); setEmail(u.email); setSearchResults([]); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) { toast.error('Please enter a valid amount'); return; }
    if (!email) { toast.error('Please enter a user email'); return; }
    setLoading(true);
    try { await requestsAPI.create(email, parsedAmount, note); setSuccess(true); toast.success('Payment request sent!'); } catch (error) { toast.error(formatApiError(error)); } finally { setLoading(false); }
  };

  const Header = () => (
    <header className="backdrop-blur-xl border-b sticky top-0 z-50" style={{ backgroundColor: t.headerBg, borderColor: t.border }}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <Link to="/" className="flex items-center gap-2"><img src={logo} alt="Divispend" className="h-10 w-auto" /></Link>
      </div>
    </header>
  );

  if (success) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: t.bg }}>
        <Header />
        <main className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md border rounded-sm shadow-none text-center" style={{ backgroundColor: t.cardBg, borderColor: t.border }} data-testid="request-success-card">
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#22C55E]" weight="fill" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Cabinet Grotesk', color: t.text }}>Request Sent!</h2>
              <p className="mb-6" style={{ color: t.textSecondary }}>You've requested ${parseFloat(amount).toFixed(2)} from {email}</p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setSuccess(false); setEmail(''); setAmount(''); setNote(''); setSelectedUser(null); }} className="flex-1 rounded-sm" style={{ borderColor: t.border, color: t.text }} data-testid="request-another-button">Request Another</Button>
                <Link to="/requests" className="flex-1"><Button className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="view-requests-button">View Requests</Button></Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: t.bg }}>
      <Header />
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border rounded-sm shadow-none" style={{ backgroundColor: t.cardBg, borderColor: t.border }} data-testid="request-money-card">
          <CardHeader className="p-8 pb-6">
            <Link to="/dashboard" className="inline-flex items-center text-sm hover:underline mb-4" style={{ color: t.textSecondary }}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
            </Link>
            <CardTitle className="text-2xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Cabinet Grotesk', color: t.text }}>
              <div className="w-10 h-10 bg-[#22C55E]/10 rounded-sm flex items-center justify-center"><Receipt className="w-5 h-5 text-[#22C55E]" weight="duotone" /></div>
              Request Money
            </CardTitle>
            <CardDescription style={{ color: t.textSecondary }}>Send a payment request to another user</CardDescription>
          </CardHeader>
          <CardContent className="p-8 pt-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2 relative">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Request From</Label>
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: t.textSecondary }} />
                  <Input type="text" placeholder="Search by name or email" value={email} onChange={(e) => handleSearch(e.target.value)} className="rounded-sm pl-10" style={{ backgroundColor: t.inputBg, borderColor: t.border, color: t.text }} data-testid="request-from-input" />
                </div>
                {searchResults.length > 0 && (
                  <div className="absolute z-10 w-full border rounded-sm shadow-lg mt-1 max-h-48 overflow-auto" style={{ backgroundColor: t.cardBg, borderColor: t.border }}>
                    {searchResults.map((u) => (
                      <button key={u.user_id} type="button" onClick={() => selectUser(u)} className="w-full px-4 py-3 text-left flex items-center gap-3 border-b last:border-0" style={{ borderColor: t.border }}>
                        <div className="w-8 h-8 bg-[#22C55E]/10 rounded-full flex items-center justify-center text-[#22C55E] font-semibold text-sm">{u.name?.[0]?.toUpperCase() || 'U'}</div>
                        <div><p className="font-medium text-sm" style={{ color: t.text }}>{u.name}</p><p className="text-xs" style={{ color: t.textSecondary }}>{u.email}</p></div>
                      </button>
                    ))}
                  </div>
                )}
                {selectedUser && (
                  <div className="flex items-center gap-3 p-3 rounded-sm" style={{ backgroundColor: t.bgSecondary }}>
                    <div className="w-8 h-8 bg-[#22C55E]/10 rounded-full flex items-center justify-center text-[#22C55E] font-semibold text-sm">{selectedUser.name?.[0]?.toUpperCase() || 'U'}</div>
                    <div className="flex-1"><p className="font-medium text-sm" style={{ color: t.text }}>{selectedUser.name}</p><p className="text-xs" style={{ color: t.textSecondary }}>{selectedUser.email}</p></div>
                    <button type="button" onClick={() => { setSelectedUser(null); setEmail(''); }} style={{ color: t.textSecondary }}>x</button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-medium" style={{ color: t.textSecondary }}>$</span>
                  <Input type="number" step="0.01" min="0.01" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-sm pl-8 text-xl font-bold" style={{ backgroundColor: t.inputBg, borderColor: t.border, color: t.text }} data-testid="request-amount-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.1em] uppercase font-semibold" style={{ color: t.textSecondary }}>Note (Optional)</Label>
                <Textarea placeholder="What's this for?" value={note} onChange={(e) => setNote(e.target.value)} className="rounded-sm resize-none" rows={3} style={{ backgroundColor: t.inputBg, borderColor: t.border, color: t.text }} data-testid="request-note-input" />
              </div>
              <Button type="submit" disabled={loading || !email || !amount} className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 font-semibold" data-testid="send-request-button">
                {loading ? 'Sending...' : `Request $${parseFloat(amount || 0).toFixed(2)}`}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
